import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Clock, DollarSign, AlertCircle, FileText, CreditCard, ArrowLeft, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PendingInvoicePage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [taxLabel, setTaxLabel] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/');
      return;
    }

    if (user.userType !== 'professional') {
      navigate('/');
      return;
    }

    fetchPendingInvoice();
    
    // Refresh every 10 seconds to update timers
    const interval = setInterval(fetchPendingInvoice, 10000);
    return () => clearInterval(interval);
  }, [user, isAuthenticated, navigate]);

  const fetchPendingInvoice = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/leads/pending-invoice`, {
        params: {
          email: user.email
        }
      });
      
      setLeads(response.data.leads || []);
      setSubtotal(response.data.subtotal || 0);
      setTax(response.data.tax || 0);
      setTaxLabel(response.data.taxLabel || 'Tax');
      setTotalAmount(response.data.total || 0);
      setLeadCount(response.data.leadCount || 0);
    } catch (error) {
      console.error('Error fetching pending invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async () => {
    try {
      setPaying(true);
      console.log('Starting payment with email:', user.email);
      console.log('API URL:', API_URL);
      
      const response = await axios.post(`${API_URL}/api/leads/pay-invoice`, {
        email: user.email,
        originUrl: window.location.origin
      }, {
        timeout: 10000 // 10 second timeout
      });
      
      console.log('Payment response:', response.data);
      
      // Redirect to Stripe checkout
      if (response.data && response.data.url) {
        console.log('Opening Stripe checkout in new tab:', response.data.url);
        // Open in new tab (works in sandboxed iframes)
        const stripeWindow = window.open(response.data.url, '_blank');
        if (!stripeWindow) {
          console.error('Popup blocked - trying direct navigation');
          window.location.href = response.data.url;
        } else {
          // Reset paying state after opening
          setPaying(false);
        }
      } else {
        console.error('No URL in response:', response.data);
        alert('Payment failed: No checkout URL received from server');
        setPaying(false);
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      console.error('Error details:', error.response?.data);
      alert(error.response?.data?.detail || error.message || 'Failed to create payment. Please try again.');
      setPaying(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = (seconds) => {
    if (seconds > 600) return 'text-green-600'; // > 10 min
    if (seconds > 300) return 'text-yellow-600'; // > 5 min
    return 'text-red-600'; // < 5 min
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/pro-quotes')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quotes
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <Home className="h-4 w-4" />
            Home
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Pending Lead Invoice</h1>
          <p className="text-gray-600">Pay for your accepted leads within 20 minutes to keep them</p>
        </div>

        {leadCount === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Pending Leads</h2>
            <p className="text-gray-600 mb-6">
              You don't have any unpaid leads at the moment.
            </p>
            <Button 
              onClick={() => navigate('/pro-quotes')}
              className="bg-orange-600 hover:bg-orange-700"
            >
              View Quote Requests
            </Button>
          </div>
        ) : (
          <>
            {/* Warning Banner */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Payment Required</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    You have {leadCount} unpaid lead{leadCount > 1 ? 's' : ''}. 
                    Pay within the time limit or they'll return to the queue for other professionals.
                  </p>
                </div>
              </div>
            </div>

            {/* Invoice Summary */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Invoice Summary</h2>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Number of Leads:</span>
                  <span className="font-semibold text-gray-900">{leadCount}</span>
                </div>
                
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700">Subtotal:</span>
                    <span className="font-medium text-gray-900">${subtotal.toFixed(2)} CAD</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700">{taxLabel}:</span>
                    <span className="font-medium text-gray-900">${tax.toFixed(2)} CAD</span>
                  </div>
                </div>
                
                <div className="border-t-2 border-gray-300 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total:</span>
                    <span className="text-2xl font-bold text-orange-600">${totalAmount.toFixed(2)} CAD</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Lead List */}
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Accepted Leads</h2>
              </div>

              <div className="divide-y divide-gray-200">
                {leads.map((lead) => (
                  <div key={lead.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {lead.service}
                          </h3>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                            ${lead.leadPrice?.toFixed(2)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{lead.location}</p>
                        <p className="text-sm text-gray-700">{lead.description}</p>
                        
                        {lead.preferredDate && (
                          <p className="text-sm text-gray-600 mt-2">
                            <span className="font-medium">Preferred Date:</span> {lead.preferredDate}
                          </p>
                        )}
                      </div>

                      {/* Timer */}
                      <div className="ml-4 text-right">
                        <div className={`flex items-center gap-2 ${getTimeColor(lead.timeRemaining)}`}>
                          <Clock className="h-5 w-5" />
                          <span className="text-2xl font-bold">
                            {formatTime(lead.timeRemaining)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Time Remaining</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Button */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Ready to Pay?
                  </h3>
                  <p className="text-sm text-gray-600">
                    Complete payment to access customer contact information
                  </p>
                </div>
                <Button
                  onClick={handlePayInvoice}
                  disabled={paying || leadCount === 0}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 text-lg"
                >
                  {paying ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Pay ${totalAmount.toFixed(2)}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Info Section */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">What happens after payment?</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li>Customer contact information will be revealed immediately</li>
                    <li>Leads will be permanently assigned to you</li>
                    <li>You can contact customers to schedule services</li>
                    <li>A receipt will be emailed to you</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PendingInvoicePage;
